import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, AlertTriangle, TrendingDown, RefreshCw, Clock, DollarSign } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInDays, isWithinInterval, addDays } from "date-fns";

interface TokenUnlock {
  id: string;
  token_symbol: string;
  token_name: string;
  unlock_date: string;
  unlock_percentage: number | null;
  unlock_value_usd: number | null;
  unlock_type: string | null;
  impact_severity: string | null;
  vesting_schedule: string | null;
}

export function TokenUnlocksCalendar() {
  const [unlocks, setUnlocks] = useState<TokenUnlock[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'high' | 'week'>('all');

  useEffect(() => {
    fetchUnlocks();
  }, []);

  const fetchUnlocks = async () => {
    try {
      setFetchError(null);
      const { data, error } = await supabase
        .from('token_unlocks')
        .select('*')
        .gte('unlock_date', new Date().toISOString())
        .order('unlock_date', { ascending: true })
        .limit(50);

      if (error) throw error;
      setUnlocks((data as TokenUnlock[]) || []);
    } catch (error: any) {
      console.error('Error fetching unlocks:', error);
      setFetchError(error.message || 'Failed to load unlock data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUnlocks = async () => {
    setIsRefreshing(true);
    try {
      const response = await supabase.functions.invoke('fetch-token-unlocks');
      if (response.data?.success) {
        await fetchUnlocks();
      }
    } catch (error) {
      console.error('Error refreshing unlocks:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredUnlocks = unlocks.filter(unlock => {
    if (filter === 'high') {
      return unlock.impact_severity === 'high' || unlock.impact_severity === 'critical';
    }
    if (filter === 'week') {
      const unlockDate = new Date(unlock.unlock_date);
      return isWithinInterval(unlockDate, {
        start: new Date(),
        end: addDays(new Date(), 7)
      });
    }
    return true;
  });

  const getSeverityColor = (severity: string | null) => {
    switch (severity) {
      case 'critical': return 'bg-destructive text-destructive-foreground';
      case 'high': return 'bg-orange/20 text-orange border-orange/30';
      case 'medium': return 'bg-warning/20 text-warning border-warning/30';
      default: return 'bg-secondary text-muted-foreground';
    }
  };

  const getUnlockTypeIcon = (type: string | null) => {
    switch (type) {
      case 'team': return '👥';
      case 'investor': return '💰';
      case 'ecosystem': return '🌐';
      case 'treasury': return '🏦';
      default: return '📦';
    }
  };

  const formatValue = (value: number | null) => {
    if (!value) return '-';
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
    return `$${(value / 1_000).toFixed(0)}K`;
  };

  const getDaysUntil = (date: string) => {
    const days = differenceInDays(new Date(date), new Date());
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  // Group unlocks by week
  const groupedUnlocks = filteredUnlocks.reduce((acc, unlock) => {
    const weekStart = format(new Date(unlock.unlock_date), 'MMM d');
    if (!acc[weekStart]) acc[weekStart] = [];
    acc[weekStart].push(unlock);
    return acc;
  }, {} as Record<string, TokenUnlock[]>);

  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            Token Unlocks Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarDays className="w-5 h-5 text-primary" />
            Tokenomics & Unlocks
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={refreshUnlocks}
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Avoid buying before major supply unlocks that can crash prices
        </p>
        
        {/* Filter buttons */}
        <div className="flex gap-2 mt-2">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('all')}
            className="text-xs h-7"
          >
            All
          </Button>
          <Button 
            variant={filter === 'week' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('week')}
            className="text-xs h-7"
          >
            This Week
          </Button>
          <Button 
            variant={filter === 'high' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('high')}
            className="text-xs h-7"
          >
            ⚠️ High Impact
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {filteredUnlocks.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />
            {fetchError ? (
              <>
                <p className="text-sm text-destructive">Failed to load unlock data</p>
                <p className="text-xs mt-1">{fetchError}</p>
              </>
            ) : (
              <p className="text-sm">No upcoming unlocks found</p>
            )}
            <Button variant="link" size="sm" onClick={refreshUnlocks}>
              {fetchError ? 'Retry' : 'Fetch unlock data'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
            {Object.entries(groupedUnlocks).slice(0, 5).map(([week, weekUnlocks]) => (
              <div key={week} className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground sticky top-0 bg-card py-1">
                  📅 Week of {week}
                </div>
                {weekUnlocks.map((unlock) => (
                  <div 
                    key={unlock.id}
                    className={`p-3 rounded-lg border transition-all hover:border-primary/50 ${
                      unlock.impact_severity === 'critical' ? 'bg-destructive/5 border-destructive/30' :
                      unlock.impact_severity === 'high' ? 'bg-orange/5 border-orange/20' :
                      'bg-secondary/50 border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getUnlockTypeIcon(unlock.unlock_type)}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">{unlock.token_symbol}</span>
                            <Badge className={`text-[10px] px-1.5 py-0 ${getSeverityColor(unlock.impact_severity)}`}>
                              {unlock.impact_severity?.toUpperCase() || 'LOW'}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{unlock.token_name}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {getDaysUntil(unlock.unlock_date)}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          {format(new Date(unlock.unlock_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-border/50">
                      {unlock.unlock_percentage && (
                        <div className="flex items-center gap-1">
                          <TrendingDown className="w-3 h-3 text-destructive" />
                          <span className="text-xs font-medium text-destructive">
                            {unlock.unlock_percentage.toFixed(1)}% supply
                          </span>
                        </div>
                      )}
                      {unlock.unlock_value_usd && (
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {formatValue(unlock.unlock_value_usd)}
                          </span>
                        </div>
                      )}
                      {unlock.unlock_type && (
                        <span className="text-xs text-muted-foreground capitalize">
                          {unlock.unlock_type}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        
        {/* Warning banner */}
        {filteredUnlocks.some(u => u.impact_severity === 'critical' || u.impact_severity === 'high') && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
            <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
            <div className="text-xs text-warning">
              <strong>Swing Trade Warning:</strong> Large unlocks can cause 10-30% price drops. 
              Consider waiting until after unlock events to enter positions.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
